import React, { useState, useEffect } from 'react';

import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Grid, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Image, Translate } from '@hydra/common/components';
import ListFeatures from '@hydra/routes/trial-ended/components/ListFeatures';

import { sendEvent } from '@hydra/utils/gtm';

import { DudaProject } from '@hydra/types';

import freeTrialExpiring from '@hydra/assets/img/free-trial-expiring.svg';

const DAYS_LEFT_SHOW_MODAL = 3;
const TIME_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours
const LOCALSTORAGE_TRIAL_MODAL_KEY = '_wr_trial_dialog_modal_seen';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    padding: 0,
    textAlign: 'center'
  },
  grid: {
    padding: theme.spacing(0, 10)
  },
  firstTitle: {
    fontWeight: theme.typography.fontWeightSemibold,
    display: 'flex',
    justifyContent: 'center',
    color: theme.palette.error.main
  },
  secondTitle: {
    fontWeight: theme.typography.fontWeightSemibold,
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2)
  },
  subtitle: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
    fontSize: theme.typography.fontSizeRoot
  },
  cta: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'center'
  },
  svg: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  svgContainer: {
    margin: '0 auto',
    width: 250,
    height: 190,
    position: 'relative'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  }
}));

export default function TrialDialog ({ dudaProject }: { dudaProject: DudaProject | null}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleClose = (event: any) => {
    localStorage.setItem(LOCALSTORAGE_TRIAL_MODAL_KEY, (Date.now()).toString());
    event.stopPropagation();
    setOpen(false);
  };

  const onClick = (event: any) => {
    handleClose(event);
    if (dudaProject && window && (window as any)._dAPI && (window as any)._dAPI.upgrade && dudaProject.upgradeDetails) {
      sendEvent('free-trial-upgrade-btn');
      (window as any)._dAPI.upgrade(dudaProject.upgradeDetails);
    }
  };

  useEffect(() => {
    try {
      const lastViewed = localStorage.getItem(LOCALSTORAGE_TRIAL_MODAL_KEY);
      const shouldShowModal = !lastViewed || (Date.now() - Number(lastViewed) > TIME_INTERVAL);
      if (shouldShowModal) {
        setOpen(true);
      }
    } catch (error) {
      setOpen(false);
    }
  }, []);

  const showModal = dudaProject && dudaProject.isPlanTrial && dudaProject.daysInTrial <= DAYS_LEFT_SHOW_MODAL;

  if (!(showModal && dudaProject)) {
    return <></>;
  }

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={open}
      onClose={(event: any, reason: string) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          handleClose(event);
        }
      }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <IconButton aria-label='close' className={classes.closeButton} onClick={handleClose}>
          <Icon type='cross' />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className={classes.svgContainer}>
          <Image src={freeTrialExpiring} className={classes.svg} />
        </div>
        <Grid container justify='center' className={classes.grid}>
          <Grid item xs={10}>
            <Typography variant='h3' component='h3' className={classes.firstTitle}>
              <Translate
                content={dudaProject.daysInTrial === 1 ? 'free_trial_modal_title_ends_day' : 'free_trial_modal_title_ends_days'}
                component='div'
                with={{ days: dudaProject.daysInTrial }}
              />
            </Typography>
            <Typography variant='h3' component='h3' className={classes.secondTitle}>
              <Translate content='free_trial_modal_title_2' component='div' />
            </Typography>
          </Grid>
          <Grid item xs={10} className={classes.subtitle}>
            <Translate content='free_trial_modal_subtitle' component='div' />
          </Grid>
          <Grid item xs={10} className={classes.subtitle}>
            <ListFeatures />
          </Grid>
          <Grid item xs={10} className={classes.cta}>
            <Button size='large' color='primary' variant='contained' onClick={onClick}>
              <Translate content='free_trial_modal_cta' />
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
